<script setup lang="ts">
  import { computed } from 'vue';
  import { IconImage } from '@/shared/ui';
  import { useFileDropzone } from '@/shared/lib';
  import {
    CERTIFICATE_ACCEPT_ATTRIBUTE,
    CERTIFICATE_ALLOWED_FORMATS_LABEL,
    CERTIFICATE_MAX_FILE_SIZE_LABEL,
  } from '../../lib/validateCertificateFile';
  import type { CertificateUploadStatus } from '../../model/useCertificateUpload';

  import ModalUploadIcon from '@/shared/assets/icons/ModalUploadIcon.svg';
  import PdfFileIcon from '@/shared/assets/icons/PdfFileIcon.svg';
  import UploadSuccessIcon from '@/shared/assets/icons/UploadSuccessIcon.svg';
  import GrayTrashIcon from '@/shared/assets/icons/GrayTrashIcon.svg';

  const props = defineProps<{
    fileName: string | null;
    uploadProgress: number;
    uploadStatus: CertificateUploadStatus;
  }>();

  const emit = defineEmits<{
    (e: 'select-file', file: File): void;
    (e: 'remove-file'): void;
  }>();

  const { isDragging, onDragEnter, onDragLeave, onDragOver, onDrop } = useFileDropzone();

  // O input é alcançado pelo DOM, e não por template ref, pelo mesmo motivo do campo de
  // currículo do ministrante: sob o Vitest deste repositório o template ref nunca é preenchido.
  function openFileDialog(event: MouseEvent): void {
    const button = event.currentTarget as HTMLElement;
    button.parentElement?.querySelector<HTMLInputElement>('input[type="file"]')?.click();
  }

  const isUploading = computed(() => props.uploadStatus === 'uploading');

  const dropzoneClasses = computed(() =>
    [
      'border border-dashed rounded-xl p-8 flex flex-col justify-center items-center gap-3',
      isDragging.value ? 'border-[var(--theme-secondary-ink)] bg-[var(--theme-secondary-tint)]' : 'border-gray-02',
    ].join(' ')
  );

  function emitFirstFile(files: FileList | null): void {
    const file = files?.[0];
    if (file) emit('select-file', file);
  }

  function handleDrop(event: DragEvent): void {
    emitFirstFile(onDrop(event));
  }

  function handleFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    emitFirstFile(input.files);
    input.value = '';
  }
</script>

<template>
  <div
    v-if="!fileName"
    data-testid="certificate-dropzone"
    :class="dropzoneClasses"
    @dragenter="onDragEnter"
    @dragleave="onDragLeave"
    @dragover="onDragOver"
    @drop="handleDrop"
  >
    <IconImage
      :src="ModalUploadIcon"
      alt="Upload"
      class="text-[var(--theme-secondary-ink)]"
    />
    <h2 class="text-[var(--theme-secondary-ink)] text-base font-semibold">
      Arraste seu arquivo para cá
    </h2>
    <p class="text-[var(--theme-text)] text-center text-sm">
      Você pode arrastar seu arquivo para esta área ou buscá-lo clicando abaixo
    </p>
    <button
      type="button"
      data-testid="certificate-search-file"
      class="text-[var(--theme-secondary-ink)] underline text-sm"
      @click="openFileDialog"
    >
      Pesquisar arquivo
    </button>
    <p
      data-testid="certificate-upload-hint"
      class="text-gray-03 text-center text-xs"
    >
      Formatos aceitos: {{ CERTIFICATE_ALLOWED_FORMATS_LABEL }}. Tamanho máximo:
      {{ CERTIFICATE_MAX_FILE_SIZE_LABEL }}.
    </p>
    <input
      data-testid="certificate-file-input"
      type="file"
      :accept="CERTIFICATE_ACCEPT_ATTRIBUTE"
      class="hidden"
      @change="handleFileChange"
    >
  </div>

  <div
    v-else
    data-testid="certificate-file-row"
    class="flex items-center gap-6"
  >
    <div class="flex min-w-0 flex-1 items-center gap-6">
      <div class="flex min-w-0 shrink-0 items-center gap-2">
        <IconImage
          :src="PdfFileIcon"
          alt="Arquivo do certificado"
          class="h-6 w-6 shrink-0"
        />
        <span
          data-testid="certificate-file-name"
          class="truncate text-sm text-gray-07"
        >{{ fileName }}</span>
      </div>
      <div class="flex min-w-0 flex-1 items-end gap-2">
        <div class="flex min-w-0 flex-1 items-center justify-end gap-3">
          <div class="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-02">
            <div
              data-testid="certificate-progress-bar"
              class="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
              :class="uploadProgress === 100 ? 'bg-green-19' : 'bg-[var(--theme-secondary)]'"
              :style="{ width: `${uploadProgress}%` }"
            />
          </div>
          <span
            data-testid="certificate-progress-label"
            class="min-w-[44px] text-right text-sm text-gray-07"
          >{{ uploadProgress }}%</span>
        </div>
        <IconImage
          v-if="uploadStatus === 'completed'"
          data-testid="certificate-upload-success"
          :src="UploadSuccessIcon"
          alt="Upload concluído"
          class="h-6 w-6 shrink-0"
        />
      </div>
    </div>
    <button
      v-if="!isUploading"
      type="button"
      data-testid="certificate-remove-file"
      aria-label="Remover certificado"
      class="text-gray-03"
      @click="emit('remove-file')"
    >
      <IconImage
        :src="GrayTrashIcon"
        alt="Remover certificado"
        class="h-6 w-6 cursor-pointer"
      />
    </button>
  </div>
</template>
